import React, { useState, useMemo } from "react"
import styled, { css } from '~/ui/styled'
import FastImage from "~/ui/FastImage"
import { useNavigation } from "@react-navigation/native"
import { AvatarWrapperTypes, AvatarProps, AvatarTypes, ImageAvatarProps, TextAvatarProps, IconAvatarProps } from "./types"
import { CenterFlex, IconFont } from "~components/El"
import { resolveAvatarUri } from "./resolveAvatarUri"
import { MessageTypes } from "~proto"

const getRadius = (size: number, type: AvatarWrapperTypes, squareRadius?: number ) => {
  if (type === AvatarWrapperTypes.CIRCLE) return size / 2
  return squareRadius ?? 6
}

type AvatarWrapProps = {
  size: number
  radius: number
  backgroundColor?: string
  width?: number | string
  height?: number | string
}

const avatarWrapCss = css<AvatarWrapProps>`
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: ${({ radius }) => radius}px;
  background-color: ${({ theme, backgroundColor }) => backgroundColor || theme.colors.success};
  width: ${({ width, size }) => (width != null ? width : `${size}px`)};
  height: ${({ height, size }) => (height != null ? height : `${size}px`)};
`

const BaseWrap = styled(CenterFlex)<AvatarWrapProps>`
  ${avatarWrapCss}
`

const BasePressableWrap = styled(CenterFlex)<AvatarWrapProps>`
  ${avatarWrapCss}
`


const Image = styled(FastImage)<Pick<ImageAvatarProps, "size">>`
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
`

const Text = styled.Text<{ size: number; color: string }>`
  font-size: ${({ size }) => size || 14}px;
  color: ${({ theme, color }) => color || theme.colors.textLight};
  font-weight: 600;
`

const getAvatarUri = resolveAvatarUri

const ImageAvatar: React.FC<ImageAvatarProps> = ({
  uri,
  size,
  onError,
  onLoad,
  onLoadStart,
  onLoadEnd,
}) => {
  const resolvedUri = getAvatarUri(uri)
  if (!resolvedUri) return null
  return (
    <Image
      src={resolvedUri}
      size={size}
      onError={onError}
      onLoad={onLoad}
      onLoadStart={onLoadStart}
      onLoadEnd={onLoadEnd}
    />
  )
}
const TextAvatar: React.FC<TextAvatarProps> = ({ name, size, color }) => {
  return <Text size={size} color={color}>{name ? name[0] : ""}</Text>
}

const IconAvatar: React.FC<IconAvatarProps> = ({ icon, size = 16, color }) => {
  return <IconFont name={icon} color={color} size={size} />
}

const PreviewAvatar: React.FC<AvatarProps> = ({
  avatar,
  icon,
  name,
  size = 40,
  color = "#fff",
  type = AvatarWrapperTypes.CIRCLE,
  squareRadius,
  backgroundColor,
  onClick,
}) => {
  const navigation = useNavigation<any>()
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)

  const avatarType = useMemo(() => {
    if (avatar && !error) return AvatarTypes.IMAGE
    if (icon) return AvatarTypes.ICON
    return AvatarTypes.TEXT
  }, [avatar, error, icon])

  const radius = useMemo(
    () => getRadius(size, type, squareRadius),
    [size, type, squareRadius],
  )

  const pressable = !!onClick || (!!avatar && !error)
  const Wrapper: any = pressable ? BasePressableWrap : BaseWrap

  const handlePress = () => {
    if (onClick) return onClick()
    if (!avatar || error) return

    const isLocal = /^(blob|file|content|data):/i.test(avatar)
    navigation.navigate("ImageGallery", {
      images: [
        {
          type: MessageTypes.IMAGE,
          key: `avatar-${avatar}`,
          url: avatar,
          ...(isLocal ? { attach_path: avatar } : {}),
        },
      ],
      index: 0,
    })
  }

  return (
    <Wrapper
      size={size}
      radius={radius}
      backgroundColor={backgroundColor}
      {...(pressable ? { onClick: handlePress } : {})}
    >
      {avatarType === AvatarTypes.IMAGE && (
        <ImageAvatar
          uri={avatar!}
          size={size}
          onLoadStart={() => setLoading(true)}
          onLoadEnd={() => setLoading(false)}
          onError={() => {
            setLoading(false)
            setError(true)
          }}
        />
      )}

      {/* loading 覆盖层（保持图片挂载，避免闪烁） */}
      {loading && (<IconFont name="loanding" size={Math.floor(size * 0.4)} color={color} />)}

      {avatarType === AvatarTypes.ICON && (
        <IconAvatar
          icon={icon}
          size={Math.floor(size * 0.4)}
          color={color}
        />
      )}

      {avatarType === AvatarTypes.TEXT && (
        <TextAvatar
          name={name}
          size={Math.floor(size * 0.4)}
          color={color}
        />
      )}
    </Wrapper>
  )
}
export default PreviewAvatar
